import clsx from 'clsx';

import PropTypes from 'prop-types';

// Component
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import ComparativeTable from '@/components/composite/Tables/ComparativeTableTemplate';

const TH_VARIANTS = {
    default: 'mb-1 ml-0.5 mr-0.5 last:mr-0 last:rounded-tr-md rounded-[1px]',
    orange: 'bg-icoOrange-100 dark:bg-icoOrange/20 border-2 border-white dark:border-icoDarkMode-anthracite rounded-[1px] rounded-tl-md',
    blue: 'bg-icoBlue-100 dark:bg-icoBlue/20 border-2 border-white dark:border-icoDarkMode-anthracite rounded-[1px] rounded-tr-md'
};

const DefaultComparativeTable = ({
    data,
    tableConfig,
    userNumbersFormat,
    hasFixWidth = false,
    isSticky = true,
    ...restProps
}) => {
    let headerWidth = 'flex-1 min-w-[11.25rem]';
    let colWidth = 'flex-1 min-w-[11rem]';
    if (hasFixWidth) {
        headerWidth = 'w-[11.25rem]';
        colWidth = 'w-[11rem]';
    }

    return (
        <>
            <ComparativeTable
                headerWidth={headerWidth}
                colWidth={colWidth}
                tableConfig={tableConfig}
                userNumbersFormat={userNumbersFormat}
                hasFixWidth={hasFixWidth}
                isSticky={isSticky}
                {...restProps}>
                <ComparativeTable.Header data={data}>
                    <ComparativeTable.HeaderTitle />
                    {data &&
                        data.length > 0 &&
                        data.map((col, id) => (
                            <ComparativeTable.HeaderItem
                                key={id}
                                className={clsx(TH_VARIANTS[col.thVariant ?? 'default'], {
                                    'sticky left-[180px] z-1 ml-0 overflow-hidden rounded-tl-md bg-icoBlue-100 dark:bg-icoDarkMode-deepBlack':
                                        col?.isFirstCol,
                                    'bg-icoGray-100 dark:bg-icoDarkMode-deepBlack': !col?.isFirstCol
                                })}>
                                {col?.isFirstCol ? (
                                    <span className="absolute inset-0 bg-icoBlue-100 dark:bg-icoBlue/20" />
                                ) : null}
                                {col?.link ? (
                                    <LinkBase
                                        href={col.link}
                                        className="group flex w-full flex-col items-center px-5 py-6 font-GSemiBold text-sm"
                                        disableFocusRing>
                                        <HeaderItemContent
                                            col={col}
                                            className="group-hover:text-icoBlue"
                                        />
                                    </LinkBase>
                                ) : (
                                    <span className="flex flex-col items-center px-5 py-6 font-GSemiBold text-sm">
                                        <HeaderItemContent col={col} />
                                    </span>
                                )}
                            </ComparativeTable.HeaderItem>
                        ))}
                </ComparativeTable.Header>

                <ComparativeTable.Body>
                    {tableConfig?.rows.map((row, rowId) => (
                        <ComparativeTable.Row key={rowId} row={row}>
                            {data?.map((col, colId) => (
                                <ComparativeTable.RowItem key={colId} row={row} col={col} />
                            ))}
                        </ComparativeTable.Row>
                    ))}
                </ComparativeTable.Body>
            </ComparativeTable>
        </>
    );
};

DefaultComparativeTable.propTypes = {
    userNumbersFormat: PropTypes.object.isRequired,
    data: PropTypes.array.isRequired,
    tableConfig: PropTypes.object.isRequired,
    hasFixWidth: PropTypes.bool,
    isSticky: PropTypes.bool
};

const HeaderItemContent = ({ col, className }) => (
    <>
        {col?.icon ? (
            <Icon size="base" icon={col?.icon?.name} className={col?.icon?.style} />
        ) : (
            <Avatar
                url={col?.picture}
                fallback={col?.name?.charAt(0) || col?.username?.charAt(0)}
                size="sm"
                className="mb-2"
            />
        )}
        <span className={clsx('z-0', className)}>{col?.name || col?.username}</span>
    </>
);

HeaderItemContent.propTypes = {
    col: PropTypes.object,
    className: PropTypes.string
};

export default DefaultComparativeTable;
